import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { API } from '@/config'
import { IJwt, IToken } from '@/store/models/auth.model'
import i18n from '@/i18n'

export const AuthErrorMsg = i18n.t('unauthorized')

interface IResData<T = any> {
    backUrl?: string | null
    code: number
    data: T
    msg?: string | null
    message?: string | null
}

const setHeader = (config: AxiosRequestConfig) => {
    const lang = localStorage.getItem('lang')
    if (lang) {
        if (lang === 'en') {
            config.headers['Accept-Language'] = 'en-US'
        } else {
            config.headers['Accept-Language'] = 'zh-CN'
        }
    } else {
        config.headers['Accept-Language'] = navigator.language || 'zh-CN'
    }
    const userInfoStr: string | null = localStorage.getItem('userInfo')
    const userInfoLocal: IJwt | IToken | null = userInfoStr && JSON.parse(userInfoStr)
    if (userInfoLocal) {
        // 已有 token ，则添加到headers
        config.headers.accept_token = userInfoLocal.token
        if ('nickName' in userInfoLocal) {
            config.headers.fullName = encodeURIComponent(userInfoLocal.nickName)
            config.headers.userId = userInfoLocal.login
        }
        return config
    } else {
        return false
    }
}

const axiosInstance = axios.create({
    baseURL: API
})
const axiosInstanceFile = axios.create({
    baseURL: API
})
const axiosInstanceAuth = axios.create({
    baseURL: API
})
const axiosInstanceLogin = axios.create({
    baseURL: 'http://10.217.2.225:3010'
    // baseURL: 'http://localhost:3010'
})

axiosInstance.interceptors.request.use(function (config) {
    const configRes = setHeader(config)
    if (!configRes) {
        // 返回验证错误, 如果本地没有 token 值, 则直接显示默认错误信息
        return Promise.reject(AuthErrorMsg + '--' + config.url + '--' + Math.random())
    }
    return configRes
}, function (error) {
    return Promise.reject(error + '--' + Math.random())
})
// 响应拦截器
axiosInstance.interceptors.response.use(function (response) {
    return response
}, function (error) {
    if (error.message && error.message.includes('401')) {
        return Promise.reject(AuthErrorMsg + '--' + Math.random())
    }
    return Promise.reject(error + '--' + Math.random())
})

// 文件下载接口
axiosInstanceFile.interceptors.request.use(function (config) {
    const configRes = setHeader(config)
    if (!configRes) {
        // 返回验证错误, 如果本地没有 token 值, 则直接显示默认错误信息
        return Promise.reject(AuthErrorMsg + '--' + config.url + '--' + Math.random())
    }
    configRes.responseType = config.responseType || 'arraybuffer'
    return configRes
}, function (error) {
    return Promise.reject(error + '--' + Math.random())
})
axiosInstanceFile.interceptors.response.use(function (response) {
    return response
}, function (error) {
    return Promise.reject(error + '--' + Math.random())
})
export const requestFile = (config: AxiosRequestConfig): Promise<{
    data: any
    type: string
    suffix: string
    fileName: string
}> => {
    return axiosInstanceFile.request<IResData>(config).then((res: AxiosResponse) => {
        if (res.status !== 200) {
            return Promise.reject(res + '--' + Math.random())
        }
        return {
            data: res.data,
            type: res.headers['content-type'],
            suffix: res.headers['content-disposition'].split('.').pop().replace('"', ''),
            fileName: res.headers['content-disposition'].split('.')?.[0].split('=').pop().replace('"', '')
        }
    })
}

// 登录相关接口
// 用户验证
axiosInstanceAuth.interceptors.request.use(function (config) {
    let url = config.url as string
    if (config.method?.toUpperCase() === 'GET' && config.params) {
        url += '?'
        const keys = Object.keys(config.params)
        for (const key of keys) {
            url += `${key}=${encodeURIComponent(config.params[key])}&`
        }
        url = url.substring(0, url.length - 1)
        config.params = {}
    }
    config.url = url
    if (config.url?.includes('usergroup/member') // 通过 groupname 获取用户
        || config.url?.includes('/user') // 通过 uidq 或 email 查询用户
    ) {
        const configRef = setHeader(config)
        if (!configRef) {
            // 返回验证错误, 如果本地没有 token 值, 则直接显示默认错误信息
            return Promise.reject(AuthErrorMsg + '--' + config.url + '--' + Math.random())
        } else {
            return configRef
        }
    }
    return config
})
axiosInstanceAuth.interceptors.response.use(function (response) {
    return response
}, function (error) {
    return Promise.reject(error + '--' + Math.random())
})
export const requestAuth = <T = any>(config: AxiosRequestConfig): Promise<T> => {
    return axiosInstanceAuth.request<IResData>(config).then((res: AxiosResponse) => {
        if (res.status !== 200) {
            return Promise.reject(res + '--' + Math.random())
        }
        const { code, msg, data, message } = res.data as IResData<T>
        if (code !== 0 && code !== 200) {
            return Promise.reject((msg || message) + '--' + Math.random())
        }
        return data
    })
}

export const requestLogin = (config: AxiosRequestConfig): Promise<any> => {
    return axiosInstanceLogin.request<IResData>(config).then((res: AxiosResponse) => {
        return res.data
    })
}

export default <T = any>(config: AxiosRequestConfig): Promise<T> => {
    return axiosInstance.request<IResData>(config).then((res: AxiosResponse) => {
        if (res.status !== 200) {
            return Promise.reject(res + '--' + Math.random())
        }
        const { code, msg, data, message } = res.data as IResData<T>
        if (code !== undefined) {
            if (code !== 0 && code !== 200) {
                return Promise.reject((msg || message) + '--' + Math.random())
            }
            return data
        } else {
            return res.data
        }
    })
}
